import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Trophy, Loader2, Users, MapPin, Calendar, Flag, CheckCircle2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { gamesApi } from '../api';

type Preview = {
  name: string; course_name: string; scheduled_date: string; holes: number;
  status: string; creator_name: string; player_count: number; is_full: boolean; expired: boolean;
};

const JoinGame: React.FC = () => {
  const { code = '' } = useParams();
  const { user, isLoading } = useAuth();
  const navigate = useNavigate();

  const [preview, setPreview] = useState<Preview | null>(null);
  const [error, setError] = useState('');
  const [joining, setJoining] = useState(false);
  const [requested, setRequested] = useState(false);
  const [checkingApproval, setCheckingApproval] = useState(false);

  useEffect(() => {
    if (!code) return;
    gamesApi.getInvitePreview(code)
      .then(setPreview)
      .catch(() => setError('This invite link is invalid or has expired.'));
  }, [code]);

  // If we arrived here logged in, clear any stashed code so we don't loop back.
  useEffect(() => {
    if (user) { try { localStorage.removeItem('pendingJoinCode'); } catch { /* ignore */ } }
  }, [user]);

  // Once the request is approved the game appears in this golfer's real game
  // list. Detect that lightweight signal and take them straight into the round.
  useEffect(() => {
    if (!requested || !user || !code) return;
    let checkInFlight = false;
    let mounted = true;
    const checkApproval = async () => {
      if (document.visibilityState !== 'visible' || checkInFlight) return;
      checkInFlight = true;
      if (mounted) setCheckingApproval(true);
      try {
        const games = await gamesApi.getMyGames();
        const joinedGame = games.find((game) => game.invite_code === code);
        if (joinedGame) navigate(`/game/${joinedGame.id}`, { replace: true });
      } catch {
        // Stay on the waiting screen and retry after a transient network error.
      } finally {
        checkInFlight = false;
        if (mounted) setCheckingApproval(false);
      }
    };
    void checkApproval();
    const timer = window.setInterval(checkApproval, 1500);
    window.addEventListener('focus', checkApproval);
    document.addEventListener('visibilitychange', checkApproval);
    return () => {
      mounted = false;
      window.clearInterval(timer);
      window.removeEventListener('focus', checkApproval);
      document.removeEventListener('visibilitychange', checkApproval);
    };
  }, [requested, user, code, navigate]);

  const goAuth = (path: string) => {
    try { localStorage.setItem('pendingJoinCode', code); } catch { /* ignore */ }
    navigate(path);
  };

  const doJoin = async () => {
    setJoining(true);
    setError('');
    try {
      const res = await gamesApi.joinByInvite(code);
      if (res.status === 'joined' && res.game_id) {
        navigate(`/game/${res.game_id}`);
      } else {
        // Pending host confirmation.
        setRequested(true);
        setJoining(false);
      }
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Could not join this game.');
      setJoining(false);
    }
  };

  const dateLabel = preview?.scheduled_date
    ? new Date(preview.scheduled_date).toLocaleString(undefined, { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })
    : '';

  return (
    <div className="app-shell bg-gradient-to-br from-green-950 via-green-900 to-black flex items-center justify-center px-4">
      <div className="w-full max-w-sm">
        <div className="flex items-center justify-center mb-6">
          <div className="bg-gradient-to-br from-green-700 to-green-950 p-3 rounded-2xl border border-green-600/70 shadow-xl">
            <Trophy className="h-7 w-7 text-yellow-400" />
          </div>
        </div>

        <div className="bg-black/40 backdrop-blur-md rounded-3xl p-6 shadow-2xl border border-green-800/50 text-center">
          {error ? (
            <>
              <Flag className="h-12 w-12 text-red-400 mx-auto mb-3" />
              <h1 className="text-xl font-bold text-white mb-1">{error}</h1>
              <Link to="/" className="mt-4 inline-block text-green-300 hover:text-white text-sm">Go to FairPlay</Link>
            </>
          ) : !preview || isLoading ? (
            <div className="py-8">
              <Loader2 className="h-8 w-8 text-emerald-400 mx-auto animate-spin" />
              <p className="text-green-200/80 mt-3">Loading invite…</p>
            </div>
          ) : requested ? (
            <>
              <CheckCircle2 className="h-14 w-14 text-emerald-500 mx-auto mb-3" />
              <h1 className="text-xl font-bold text-white mb-1">Request sent 🎉</h1>
              <p className="text-green-100/90 mb-3">{preview.creator_name} just needs to confirm you.</p>
              <div className="mb-5 inline-flex items-center gap-2 rounded-full bg-emerald-400/10 px-3 py-2 text-xs font-semibold text-emerald-200" role="status" aria-live="polite">
                <Loader2 className={`h-4 w-4 ${checkingApproval ? 'animate-spin' : ''}`} />
                Waiting for the host — we'll take you straight in
              </div>
              <Link to="/" className="inline-block bg-white/10 border border-white/20 text-white px-6 py-3 rounded-xl font-semibold hover:bg-white/20 transition">
                Go to FairPlay
              </Link>
            </>
          ) : (
            <>
              <p className="text-green-300/80 text-sm mb-1">{preview.creator_name} invited you to a game</p>
              <h1 className="text-2xl font-bold text-white mb-4">{preview.name}</h1>

              <div className="space-y-2 text-left bg-white/5 rounded-2xl p-4 mb-5">
                <div className="flex items-center text-green-100/90 text-sm"><MapPin className="h-4 w-4 mr-2 text-emerald-400" />{preview.course_name}</div>
                <div className="flex items-center text-green-100/90 text-sm"><Calendar className="h-4 w-4 mr-2 text-emerald-400" />{dateLabel}</div>
                <div className="flex items-center text-green-100/90 text-sm"><Users className="h-4 w-4 mr-2 text-emerald-400" />{preview.player_count}/4 players · {preview.holes} holes</div>
              </div>

              {preview.expired ? (
                <p className="text-yellow-300 text-sm mb-4">This invite link has expired. Ask the host for a fresh one.</p>
              ) : preview.is_full ? (
                <p className="text-yellow-300 text-sm mb-4">This game is full (4 players).</p>
              ) : preview.status !== 'pending' ? (
                <p className="text-yellow-300 text-sm mb-4">This game has already started — ask the host to add you.</p>
              ) : user ? (
                <>
                  <button
                    onClick={doJoin}
                    disabled={joining}
                    className="w-full bg-gradient-to-r from-green-600 to-emerald-600 text-white py-3.5 rounded-xl font-semibold hover:from-green-500 hover:to-emerald-500 transition disabled:opacity-50"
                  >
                    {joining ? 'Requesting…' : 'Request to join'}
                  </button>
                  <p className="text-xs text-green-300/70 mt-2">The host confirms who's in.</p>
                </>
              ) : (
                <div className="space-y-2">
                  <button onClick={() => goAuth('/register')} className="w-full bg-gradient-to-r from-green-600 to-emerald-600 text-white py-3.5 rounded-xl font-semibold hover:from-green-500 hover:to-emerald-500 transition">
                    Create account & join
                  </button>
                  <button onClick={() => goAuth('/login')} className="w-full bg-white/10 border border-white/20 text-white py-3.5 rounded-xl font-semibold hover:bg-white/20 transition">
                    I already have an account
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default JoinGame;
